import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, IconButton, MenuItem, Paper, Rating, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import api from "../../api";
import LoadError from "../../components/admin/LoadError";

const EMPTY = {
  display_name: "",
  child_year: "",
  exam_board: "",
  rating: 5,
  quote: "",
  is_active: true,
  sort_order: 0,
};

const STATUS_COLORS = { approved: "success", pending: "warning", rejected: "default" };

/** Live on the home page only when approved + consented + active. */
const isLive = (row) => row.status === "approved" && row.consent_to_publish && row.is_active;

export default function TestimonialsManage({ canManage = true }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [toDelete, setToDelete] = useState(null);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = { page: page + 1, per_page: perPage, sort: "display" };
      if (query) params.search = query;
      if (active !== "") params.active = active;
      const res = await api.get("admin/testimonials", { params });
      setRows(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      setRows([]);
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, query, active]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      display_name: row.display_name || "",
      child_year: row.child_year || "",
      exam_board: row.exam_board || "",
      rating: row.rating || 5,
      quote: row.quote || "",
      is_active: !!row.is_active,
      sort_order: row.sort_order ?? 0,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setErrors({});
    const payload = {
      ...form,
      child_year: form.child_year.trim() || null,
      exam_board: form.exam_board.trim() || null,
      rating: Number(form.rating) || 5,
      sort_order: form.sort_order === "" ? 0 : Number(form.sort_order),
    };
    try {
      if (editing) {
        await api.patch(`admin/testimonials/${editing.id}`, payload);
        toast.success("Testimonial updated");
      } else {
        await api.post("admin/testimonials", payload);
        toast.success(payload.is_active ? "Testimonial created - now on the home page" : "Testimonial created (inactive)");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      const errs = e.response?.data?.errors || {};
      setErrors(Object.fromEntries(Object.entries(errs).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])));
      toast.error(e.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    const next = !row.is_active;
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, is_active: next } : r)));
    try {
      await api.patch(`admin/testimonials/${row.id}/active`, { is_active: next });
      toast.success(next ? "Activated" : "Deactivated - hidden from the home page");
    } catch (e) {
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, is_active: !next } : r)));
      toast.error(e.response?.data?.message || "Could not update");
    }
  };

  const confirmDelete = async () => {
    const row = toDelete;
    setToDelete(null);
    try {
      await api.delete(`admin/testimonials/${row.id}`);
      toast.success("Testimonial deleted");
      if (rows.length === 1 && page > 0) setPage(page - 1);
      else load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2} alignItems={{ sm: "center" }}>
        <TextField
          size="small"
          label="Search name, text or label"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, bgcolor: "#fff" }}
        />
        <TextField
          select
          size="small"
          label="Active"
          value={active}
          onChange={(e) => {
            setActive(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 140, bgcolor: "#fff" }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="1">Active</MenuItem>
          <MenuItem value="0">Inactive</MenuItem>
        </TextField>
        <Box flex={1} />
        <Tooltip title={canManage ? "" : "You need the manage-testimonials permission"}>
          <span>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={!canManage}
              sx={{ bgcolor: canManage ? "#3B2A9F" : undefined, whiteSpace: "nowrap" }}
            >
              Add testimonial
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view testimonials but not add or change them. Ask a Super Admin to give your role the
          "manage-testimonials" permission (Roles &amp; Permissions), then log out and back in.
        </Alert>
      )}

      {loadError ? (
        <LoadError what="testimonials" error={loadError} onRetry={load} />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Testimonial</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Order</TableCell>
                  <TableCell align="center">Active</TableCell>
                  {canManage && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      {canManage && !query && active === ""
                        ? "No testimonials yet. Click \"Add testimonial\" to create the first one."
                        : "No testimonials found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Typography fontWeight={700} variant="body2">{row.display_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[row.child_year, row.exam_board].filter(Boolean).join(" · ")}
                        </Typography>
                        <Box>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={row.source === "admin" ? "Added by admin" : "From app"}
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{row.quote}</Typography>
                      </TableCell>
                      <TableCell>
                        <Rating value={row.rating} readOnly size="small" />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <Chip size="small" color={STATUS_COLORS[row.status] || "default"} label={row.status} />
                          {!row.consent_to_publish && <Chip size="small" label="No consent" />}
                          {isLive(row) && <Chip size="small" color="primary" variant="outlined" label="Live" />}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">{row.sort_order ?? 0}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={row.is_active ? "Shown (if approved and consented)" : "Hidden from the home page"}>
                          <span>
                            <Switch
                              checked={!!row.is_active}
                              onChange={() => toggleActive(row)}
                              disabled={!canManage}
                              size="small"
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      {canManage && (
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setToDelete(row)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
          />
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit testimonial" : "Add testimonial"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Name"
              required
              value={form.display_name}
              onChange={set("display_name")}
              error={!!errors.display_name}
              helperText={errors.display_name || "As shown on the site, e.g. Sarah M."}
              inputProps={{ maxLength: 80 }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Role / label"
                value={form.child_year}
                onChange={set("child_year")}
                error={!!errors.child_year}
                helperText={errors.child_year || "e.g. Parent of Year 5"}
                inputProps={{ maxLength: 40 }}
                fullWidth
              />
              <TextField
                label="Exam board / extra tag"
                value={form.exam_board}
                onChange={set("exam_board")}
                error={!!errors.exam_board}
                helperText={errors.exam_board || "Optional, e.g. GL or CSSE"}
                inputProps={{ maxLength: 40 }}
                fullWidth
              />
            </Stack>
            <TextField
              label="Testimonial"
              required
              multiline
              minRows={4}
              value={form.quote}
              onChange={set("quote")}
              error={!!errors.quote}
              helperText={errors.quote || `${form.quote.length}/1000`}
              inputProps={{ maxLength: 1000 }}
            />
            <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
              <Box>
                <Typography variant="body2" color={errors.rating ? "error" : "text.secondary"}>Rating</Typography>
                <Rating
                  value={Number(form.rating) || 0}
                  onChange={(_, v) => setForm((f) => ({ ...f, rating: v || 1 }))}
                />
              </Box>
              <TextField
                label="Display order"
                type="number"
                size="small"
                value={form.sort_order}
                onChange={set("sort_order")}
                error={!!errors.sort_order}
                helperText={errors.sort_order || "Lower shows first"}
                inputProps={{ min: 0 }}
                sx={{ width: 150 }}
              />
              <Stack direction="row" alignItems="center">
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
                <Typography variant="body2">Active (show on home page)</Typography>
              </Stack>
            </Stack>
            {editing && editing.source !== "admin" && (
              <Typography variant="caption" color="text.secondary">
                Sent from the app. It shows on the home page only if it is approved, consent was given and it is active.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ bgcolor: "#3B2A9F" }}>
            {saving ? "Saving..." : editing ? "Save changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
        <DialogTitle>Delete testimonial?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the testimonial from {toDelete?.display_name || "this person"}. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
